import { FC, ReactNode, useEffect, useMemo, useState } from "react";

import { Box, Spinner, Text } from "@hightouch/htui";
import { useFormContext, useWatch } from "react-hook-form";
import { useFlags } from "launchdarkly-react-client-sdk";

import {
  ShowModifierNode,
  ResetModifierNode,
  AsyncReferenceModifierNode,
  ModifierType,
  FormkitModifier,
  resolveEntity,
  isFormkitBoolean,
  resolveBooleanCondition,
} from "@hightouch/formkit";
import { formkitRenderer } from "@hightouch/lib/templating/formkit-renderer";

import { FieldError } from "src/components/field-error";
import {
  collectStateReferenceKeys,
  getNestedKeysWithDefault,
} from "src/formkit/component-utils";
import { useQuery } from "src/formkit/hooks";
import { useFormkitContext } from "./formkit-context";

type Props = {
  children: ReactNode;
  node: FormkitModifier;
};

type ShowProps = {
  children: ReactNode;
  node: ShowModifierNode;
};

type ResetProps = {
  children: ReactNode;
  node: ResetModifierNode;
};

type AsyncReferenceProps = {
  children: ReactNode;
  node: AsyncReferenceModifierNode;
};

const ShowModifier: FC<Readonly<ShowProps>> = ({ children, node }) => {
  const baseContext = useFormkitContext();
  const featureFlags = useFlags();
  const context = useMemo(
    () => ({ ...baseContext, featureFlags }),
    [baseContext, featureFlags],
  );
  const { watch } = useFormContext();

  const condition = node.condition;
  // A Show condition reads form state only through StateReferences.
  // Subscribe to just those fields so unrelated changes do not re-render it.
  const conditionKeys = useMemo(
    () => collectStateReferenceKeys(condition),
    [condition],
  );
  useWatch({ name: conditionKeys });

  // Fields write their defaults in mount effects, before the useWatch subscription exists.
  // Re-render once after mount so defaults that were already committed get observed.
  const [, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const showChildren =
    condition &&
    isFormkitBoolean(condition) &&
    resolveBooleanCondition(condition, formkitRenderer)(watch, context);

  return showChildren ? <>{children}</> : null;
};

export const ResetModifier: FC<Readonly<ResetProps>> = ({ children, node }) => {
  const { watch, setValue } = useFormContext();

  const baseContext = useFormkitContext();
  const featureFlags = useFlags();
  const context = useMemo(
    () => ({ ...baseContext, featureFlags }),
    [baseContext, featureFlags],
  );

  useEffect(() => {
    const subscription = watch((_, { name }) => {
      if (name && node?.keys?.includes(name)) {
        const defaultValues = getNestedKeysWithDefault(node, context, watch);
        for (const [key, value] of Object.entries(defaultValues)) {
          setValue(key, value);
        }
      }
    });
    return () => subscription.unsubscribe();
  });

  return <>{children}</>;
};

export const AsyncReferenceModifier: FC<Readonly<AsyncReferenceProps>> = ({
  children,
  node,
}) => {
  const baseContext = useFormkitContext();
  const featureFlags = useFlags();
  const { watch } = useFormContext();
  const context = useMemo(
    () => ({
      ...baseContext,
      featureFlags,
    }),
    [baseContext, featureFlags],
  );
  // Re-resolve the handler when fields referenced via StateReference change.
  const stateReferenceKeys = useMemo(
    () => collectStateReferenceKeys(node.handler),
    [node.handler],
  );
  const stateReferenceValues = useWatch({ name: stateReferenceKeys });
  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  const handler = useMemo(
    () =>
      resolveEntity({
        state: watch,
        context,
        entity: node.handler,
        liquid: true,
        renderer: formkitRenderer,
      }),
    [context, node.handler, watch, stateReferenceValues],
  );
  const {
    data,
    error: queryError,
    isFetching,
  } = useQuery(JSON.stringify({ name, variables: handler?.variables }), {
    fetchProps: {
      destinationId: baseContext?.destination?.id,
      modelId: baseContext?.model?.id,
      query: handler?.query ?? handler?.document,
      variables: handler?.variables,
    },
  });

  if (isFetching) {
    return <Spinner />;
  }

  if (queryError || !data) {
    return (
      <Box>
        <Text>There is a problem loading the rest of the form</Text>
        <FieldError error={queryError as any} />
      </Box>
    );
  }

  return <>{children}</>;
};

export const Modifier: FC<Readonly<Props>> = (props) => {
  if (props.node.modifier === ModifierType.AsyncReference) {
    return <AsyncReferenceModifier {...props} node={props.node} />;
  } else if (props.node.modifier === ModifierType.Show) {
    return <ShowModifier {...props} node={props.node} />;
  } else if (props.node.modifier === ModifierType.Reset) {
    return <ResetModifier {...props} node={props.node} />;
  }
  return <></>;
};
